import React, { useEffect } from "react";
import Select from "react-select";
import Card from "components/card";
import ButtonEdit from "../../../../assets/imgesDriver/ButtonEdit.svg";
import DeleteIcon from "../../../../assets/imgesDriver/deleteIcon.svg";
import "../vehicles.css";
import { useTranslation } from "react-i18next";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import block from "../../../../assets/svg/block.svg";
import completedRide from "../../../../assets/svg/completedRide.svg";
import { updateVehicleApi } from "services/customAPI";
import { toast } from "react-toastify";
import { Types } from "shared-lib";
import { VehicleTableRowDTO } from "types/api.types";

const columnHelper = createColumnHelper<VehicleTableRowDTO>();

export type ColumnsTableVehiclesProps = {
  tableData: VehicleTableRowDTO[];
  handleClickForDeleteModal: (data: VehicleTableRowDTO) => void;
  statusOptions?: { value: string; label: string; icon?: string }[];
  setVehicleStatus?: (status: Types.VehicleStatus | string) => void;
  vehicleStatus?: Types.VehicleStatus | string;
};

function ColumnsTableVehicles(props: ColumnsTableVehiclesProps) {
  const { t } = useTranslation();
  const { tableData, handleClickForDeleteModal, statusOptions, setVehicleStatus, vehicleStatus } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState<VehicleTableRowDTO[]>(tableData);
  const navigate = useNavigate();

  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  const handleEditClick = (vehicleId: number) => {
    navigate(`../vehicles/vehicleform/${String(vehicleId)}`);
  };

  const updateVehicleInState = (
    vehicleId: number,
    approved: Types.ApprovalStatus
  ) => {
    const updatedData = data.map((item) => {
      if (item.action.id === vehicleId) {
        return {
          ...item,
          action: {
            ...item.action,
            approved,
          },
        };
      }
      return item;
    });
    setData(updatedData);
  };

  async function handleApprove(vehicleId: number) {
    try {
      const driverApproved = await updateVehicleApi(vehicleId, {
        approved: Types.ApprovalStatus.APPROVED,
      });
      if (driverApproved) {
        toast.success(t("Driver approved successfully"));
        updateVehicleInState(vehicleId, Types.ApprovalStatus.APPROVED);
      }
    } catch (error) {
      alert(t("Alguma coisa deu errado!"));
    }
  }

  const handleClickForRejectDoc = async (vehicleId: number) => {
    try {
      const driverRejected = await updateVehicleApi(vehicleId, {
        approved: Types.ApprovalStatus.REJECTED,
      });
      if (driverRejected) {
        toast.success(t("Driver rejected successfully"));
        updateVehicleInState(vehicleId, Types.ApprovalStatus.REJECTED);
      }
    } catch (error) {
      alert(t("Alguma coisa deu errado!"));
    }
  };

  const columns = [
    columnHelper.accessor("vehicleName", {
      id: "vehicleName",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Vehicle Nickname")}
        </p>
      ),
      cell: (info) => (
        <p
          className="text-sm font-bold text-white"
          style={{ display: "flex", alignItems: "center" }}
        >
          {info.getValue()?.path === "" ? (
            <div
              style={{
                width: "40px",
                height: "40px",
                padding: "1px",
                borderRadius: "8px",
                display: "inline-block",
                marginRight: "6px",
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            >
              <span
                style={{
                  position: "relative",
                  left: "14px",
                  top: "8.5px",
                  fontSize: "18px",
                  color: "#a0aec0",
                }}
              >
                {info.getValue()?.name.slice(0, 1).toUpperCase()}
              </span>
            </div>
          ) : (
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                display: "inline-block",
                marginRight: "6px",
                alignItems: "center",
              }}
            >
              <img
                src={info.getValue()?.path}
                style={{
                  objectFit: "fill",
                  height: "100%",
                  width: "auto",
                  borderRadius: "4px",
                }}
                alt="img"
              />
            </div>
          )}
          <span className="text-white">{info.getValue()?.name}</span>
        </p>
      ),
    }),
    columnHelper.accessor("vehicleNumber", {
      id: "vehicleNumber",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Vehicle Number")}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("vehicleType", {
      id: "vehicleType",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Vehicle Type")}
        </p>
      ),
      cell: (info) => {
        const cat = info.getValue();
        const row = info.row.original;
        if (!cat) return <p className="text-sm font-bold text-gray-400">N/A</p>;

        const transportModes = row.transportModes ?? [];
        const primaryMode = row.primaryTransportMode ?? "";
        const vehicleTypeName = row.vehicleTypeName ?? "";

        return (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-bold text-white">{cat?.name || "N/A"}</p>
            {vehicleTypeName && (
              <p className="text-xs text-gray-400 capitalize">{vehicleTypeName}</p>
            )}
            <div className="flex flex-wrap gap-1">
              {transportModes.length > 0
                ? transportModes.map((mode) => {
                  const isPrimary = mode === primaryMode;
                  const isCargo = mode === Types.TransportMode.CARGO;
                  return (
                    <span
                      key={mode}
                      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold"
                      style={{
                        backgroundColor: isCargo
                          ? "rgba(245,158,11,0.15)"
                          : "rgba(59,130,246,0.15)",
                        color: isCargo ? "#F59E0B" : "#60A5FA",
                        border: `1px solid ${isCargo ? "#F59E0B" : "#60A5FA"}${isPrimary ? "88" : "44"}`,
                        fontWeight: isPrimary ? 700 : 500,
                      }}
                    >
                      {Types.getTransportModeEmoji(mode)}{" "}
                      {Types.getTransportModeLabel(mode)}
                      {isPrimary && (
                        <span className="text-[9px] opacity-80 ml-0.5">★</span>
                      )}
                    </span>
                  );
                })
                : cat.transportMode && (
                  <span
                    className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold"
                    style={{
                      backgroundColor:
                        cat.transportMode === Types.TransportMode.CARGO
                          ? "rgba(245,158,11,0.15)"
                          : "rgba(59,130,246,0.15)",
                      color: cat.transportMode === Types.TransportMode.CARGO ? "#F59E0B" : "#60A5FA",
                      border: `1px solid ${cat.transportMode === Types.TransportMode.CARGO ? "#F59E0B44" : "#60A5FA44"}`,
                    }}
                  >
                    {Types.getTransportModeEmoji(cat.transportMode)}{" "}
                    {Types.getTransportModeLabel(cat.transportMode)}
                  </span>
                )
              }
            </div>
          </div>
        );
      },
    }),

    columnHelper.accessor("vehicleStatus", {
      id: "vehicleStatus",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Status")}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p
            className={
              info.getValue() === Types.VehicleStatus.AVAILABLE
                ? "onlineClass text-sm font-bold "
                : "offlineClass text-sm font-bold "
            }
          >
            {info.getValue() === Types.VehicleStatus.AVAILABLE
              ? t("Disponível")
              : t("Atribuído")}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("driverName", {
      id: "driver",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Driver")}
        </p>
      ),
      cell: (info) => {
        const row = info.row.original;
        const driverNameRaw = info.getValue() as string | undefined;
        const driverName = driverNameRaw?.trim();
        const hasDriver = !!(row.driverId && driverName && driverName !== "N/D" && driverName !== "");

        if (!hasDriver) {
          return <p className="text-sm font-bold text-gray-400">{t("Unassigned")}</p>;
        }

        return (
          <p
            className="text-sm font-bold text-white cursor-pointer hover:text-cyan-400 hover:underline transition-colors block max-w-[150px] truncate"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/drivers/driverform/${row.driverId}`);
            }}
            title={driverName}
          >
            {driverName}
          </p>
        );
      },
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("approve")}
        </p>
      ),
      cell: (info) => {
        const vehicle = info.getValue();
        const isApproved = vehicle?.approved === Types.ApprovalStatus.APPROVED;
        const isRejected = vehicle?.approved === Types.ApprovalStatus.REJECTED;
        const isPending = vehicle?.approved === Types.ApprovalStatus.PENDING;

        return (
          <div className="flex items-center">
            {isPending && (
              <>
                <div className="cursor-pointer">
                  <img
                    src={completedRide}
                    style={{ marginRight: "8px" }}
                    onClick={() => {
                      if (!vehicle?.id) {
                        return;
                      }
                      handleApprove(vehicle.id);
                    }}
                    alt="Approve"
                  />
                </div>
                <div className="cursor-pointer">
                  <img
                    src={block}
                    onClick={() => {
                      if (!vehicle?.id) {
                        return;
                      }
                      handleClickForRejectDoc(vehicle.id);
                    }}
                    alt="Reject"
                  />
                </div>
              </>
            )}
            {isRejected && (
              <span className="font-bold text-red-500">{t("Rejected")}</span>
            )}
            {isApproved && (
              <span className="font-bold text-green-500">{t("Approved")}</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("action", {
      id: "actionButtons",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Actions")}
        </p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <img
            src={ButtonEdit}
            alt={t("Editar")}
            className="button-edit me-2"
            onClick={() => {
              const vehicleId = info.row.original.action.id;
              if (!vehicleId) {
                return;
              }
              handleEditClick(vehicleId);
            }}
            height={30}
            width={30}
          />
          <img
            src={DeleteIcon}
            alt={t("Excluir")}
            className="button-delete"
            onClick={() => handleClickForDeleteModal(info.row.original)}
            height={30}
            width={30}
          />
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-white">
          {t("Vehicles")}
        </div>
        {statusOptions && setVehicleStatus && (
          <div className="flex gap-4 items-center">
            <Select
              className="w-48 text-sm outline-none"
              options={statusOptions}
              value={statusOptions.find((opt) => opt.value === vehicleStatus)}
              onChange={(option) => {
                if (option && setVehicleStatus) {
                  setVehicleStatus(option.value);
                }
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: "#1e1e2d",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }),
                option: (base, { isFocused, isSelected }) => ({
                  ...base,
                  backgroundColor: isSelected
                    ? "rgba(66, 153, 225, 0.2)"
                    : isFocused
                      ? "rgba(255, 255, 255, 0.05)"
                      : "transparent",
                  color: isSelected ? "#63b3ed" : "white",
                  cursor: "pointer",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: "white",
                }),
                input: (base) => ({
                  ...base,
                  color: "white",
                }),
              }}
            />
          </div>
        )}
      </header>
      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-white/10">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer pb-3 pr-4 pt-4 text-start"
                  >
                    <div className="flex gap-4 text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() === "asc" ? (
                        <FaCaretUp
                          size={20}
                          className="font-bold text-gray-600"
                        />
                      ) : header.column.getIsSorted() === "desc" ? (
                        <FaCaretDown
                          size={20}
                          className="font-bold text-gray-600"
                        />
                      ) : (
                        <div className="mr-[-6] flex">
                          <FaCaretDown
                            size={20}
                            className="font-bold text-gray-600"
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  <h2 className="m-4 text-gray-400" style={{ fontSize: "30px" }}>
                    {t("No Results!")}
                  </h2>
                </td>
              </tr>
            ) : (
              table
                .getRowModel()
                .rows.slice(0, 10)
                .map((row, idx) => (
                  <tr
                    key={row.id}
                    className={`transition-colors hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                      }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="min-w-[20%] border-b border-white/5 py-3 pr-4 text-start"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default ColumnsTableVehicles;
